import { useQueries } from '@tanstack/react-query';
import { IGetAddressDecodeResData, getAddressDecode } from 'api/misc';
import { gender, races } from '../CRForm/ApplicationInformation/constants';

interface IPieChart {
  label: string[];
  dataSets: { data: number; color: string }[];
}

export const useApplicationsGeoLocation = (data: any) => {
  const applicationsList = data?.data;

  const widgetsData: {
    totalApplications: number;
    totalHousePrice: number;
    statusSummary: { [key: string]: number };
    gender: IPieChart;
    totalToBeInspected: number;
    totalReceivedAssistance: number;
    maritalStatus: IPieChart;
    totalRaces: IPieChart;
    totalDisabled: IPieChart;
    totalFemaleHeadOfHouseHold: IPieChart;
    totalIncomeBreakDown: IPieChart;
    totalHouseBuiltBefore1978: IPieChart;
    applicationLocationList: Array<{
      id: string;
      [key: string]: string;
      fullAddress: string;
    }>;
    districtBreakdownDenied: { [key: string]: number };
  } = {
    totalApplications: 0,
    totalHousePrice: 0,
    statusSummary: {},
    totalRaces: {
      label: races,
      dataSets: [
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#0FCA98' },
        { data: 0, color: '#2B6CB0' },
        { data: 0, color: '#F27141' },
      ],
    },
    totalDisabled: {
      label: ['Yes', 'No'],
      dataSets: [
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#0FCA98' },
      ],
    },
    totalFemaleHeadOfHouseHold: {
      label: ['Yes', 'No'],
      dataSets: [
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#0FCA98' },
      ],
    },
    totalIncomeBreakDown: {
      label: gender,
      dataSets: [
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#0FCA98' },
      ],
    },
    totalHouseBuiltBefore1978: {
      label: ['Yes', 'No'],
      dataSets: [
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#0FCA98' },
      ],
    },
    gender: {
      label: ['Female', 'Male'],
      dataSets: [
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#0FCA98' },
      ],
    },
    totalToBeInspected: 0,
    totalReceivedAssistance: 0,
    maritalStatus: {
      label: ['Married', 'Unmarried', 'Widowed'],
      dataSets: [
        { data: 0, color: '#0FCA98' },
        { data: 0, color: '#D9B64E' },
        { data: 0, color: '#2B6CB0' },
      ],
    },
    applicationLocationList: [],
    districtBreakdownDenied: {},
  };

  applicationsList?.forEach((a: any) => {
    const { applicationData, id, contactEmail, contactName } = a;

    // Location
    const applicationLocation = `${applicationData.application.streetAddress}, ${applicationData.application.city}, ${applicationData.application.state}, ${applicationData.application.zipCode}`;
    widgetsData['applicationLocationList'].push({
      id: id,
      email: contactEmail,
      name: contactName,
      phone: applicationData?.application?.phone,
      fullAddress: applicationLocation,
    });
    //Total Application Widget
    widgetsData['totalApplications'] += 1;

    // Total Amount Requested
    widgetsData['totalHousePrice'] += applicationData?.propertyInformation
      ?.housePrice
      ? applicationData?.propertyInformation?.housePrice
      : 0;

    //Status Summary
    widgetsData['statusSummary'][a.status] =
      (widgetsData['statusSummary'][a.status] ?? 0) + 1;

    // Total Races
    applicationData?.application?.race.forEach((r: any) => {
      if (r === 'African-American')
        widgetsData.totalRaces.dataSets[0].data += 1;
      if (r === 'Caucasian') widgetsData.totalRaces.dataSets[1].data += 1;
      if (r === 'Hispanic') widgetsData.totalRaces.dataSets[2].data += 1;
      if (r === 'Other') widgetsData.totalRaces.dataSets[3].data += 1;
    });

    // Total Disabled
    if (applicationData?.application?.disabled === 'yes') {
      widgetsData.totalDisabled.dataSets[0].data += 1;
    }
    if (applicationData?.application?.disabled === 'no') {
      widgetsData.totalDisabled.dataSets[1].data += 1;
    }

    // Total Female Head of Household
    if (applicationData?.application?.femaleHeadOfHousehold === 'yes') {
      widgetsData.totalFemaleHeadOfHouseHold.dataSets[0].data += 1;
    }
    if (applicationData?.application?.femaleHeadOfHousehold === 'no') {
      widgetsData.totalFemaleHeadOfHouseHold.dataSets[1].data += 1;
    }

    // Total House Built Before 1978
    if (applicationData?.application?.wasBuiltBefore1978 === 'yes') {
      widgetsData.totalHouseBuiltBefore1978.dataSets[0].data += 1;
    }
    if (applicationData?.application?.wasBuiltBefore1978 === 'no') {
      widgetsData.totalHouseBuiltBefore1978.dataSets[1].data += 1;
    }

    // gender
    if (applicationData?.application?.gender === 'Female') {
      widgetsData.gender.dataSets[0].data += 1;
    }
    if (applicationData?.application?.gender === 'Male') {
      widgetsData.gender.dataSets[1].data += 1;
    }

    // marital status
    if (applicationData?.application?.maritalStatus === 'Married') {
      widgetsData.maritalStatus.dataSets[0].data += 1;
    }
    if (applicationData?.application?.maritalStatus === 'Un-Married') {
      widgetsData.maritalStatus.dataSets[1].data += 1;
    }
    if (applicationData?.application?.maritalStatus === 'Widow') {
      widgetsData.maritalStatus.dataSets[2].data += 1;
    }

    // Total To Be Inspected
    if (a.status === 'PHYSICAL_INSPECTION') {
      widgetsData.totalToBeInspected += 1;
    }

    // Total Denied - 3 Year Rule
    if (applicationData?.application?.receivedAssistance === 'yes')
      widgetsData.totalReceivedAssistance += 1;

    if (a.status === 'DENIED') {
      //District Breakdown Denied
      const { neighborhood } = applicationData?.application;
      widgetsData['districtBreakdownDenied'][neighborhood] =
        (widgetsData['districtBreakdownDenied'][neighborhood] ?? 0) + 1;
    }
  });

  const fetchApplicationLocation = useQueries({
    queries: widgetsData?.applicationLocationList?.map(
      (d: { id: string; [key: string]: string; fullAddress: string }) => {
        return {
          queryKey: ['GET_APPLICATION_WIDGET_GEO_CODE', d?.id],
          queryFn: () => getAddressDecode(d.fullAddress),
          select: (resData: { data: IGetAddressDecodeResData }) => {
            const { lat, lng } = resData.data;
            return { ...d, coordinates: { lat, lng } };
          },
        };
      }
    ),
  });
  const applicationsGeoLocated = fetchApplicationLocation?.map((d) => d.data);

  return { applicationsGeoLocated, widgetsData };
};
